import { useState, useCallback, useEffect } from 'react'
import { useUser } from '@/context/UserContext' // <-- Add this import
import { createAICommand, AICommandType, AICommandStatus } from '@/utils/aiCommandUtils'

// Define DocumentStyle type to match DocumentStyleSelector.tsx
interface DocumentStyle {
  type: 'predefined' | 'custom' | null
  value:
  | 'formal-corporate'
  | 'formal-scientific'
  | 'entertaining'
  | 'persuasive'
  | 'instructional'
  | 'narrative'
  | 'conversational'
  | 'analytical'
  | 'regulatory'
  | 'visual'
  | 'inspirational'
  | 'custom'
  | null
  customValue?: string
}

interface DocumentNodeData {
  contentMarkdown?: string
  documentDescription?: string
  statusMessage?: string
  isMarkdownExpanded?: boolean
  assignedEmails?: string[]
  configuration?: DocumentNodeConfiguration
  label?: string
  documentStyle?: DocumentStyle // Use the defined type
  context?: string
  linkedKnowledge?: string
  documentCommand?: any
  topic?: string
  sectionExplanations?: Record<string, string> // Add explanations field
  sectionExplanationStates?: Record<string, boolean> // Add explanation states field
  protectedSections?: Record<string, boolean>
  processingStates?: Record<string, boolean>
  setReferences?: Record<string, string[]>
  sectionCommand?: any
  isLocked?: boolean
  isMinimized?: boolean
  lastModified?: number
  data?: any
  selectedSection?: any
  analysisAndRecommendations?: boolean // Analysis & Recommendations flag
  analysisResults?: any // Analysis results from the service
  lastAnalyzedAt?: string // Timestamp of last analysis
  printSettings?: {
    headerText: string
    footerText: string
    coverMarkdown: string
  }
  sectionStates?: Record<string, "none" | "accepted" | "rejected" | "pending">
}

interface DocumentNodeConfiguration {
  isAutomated: boolean
  modifierprompt: string
  markdownFormatVisible: boolean
  analysisAndRecommendations?: boolean
}

const useDocumentState = (id: string, data: DocumentNodeData, updateNodeData: (id: string, data: any) => void) => {
  const user = useUser(); // <-- Add this line
  const [contentMarkdown, setContentMarkdown] = useState(data.contentMarkdown || data.documentDescription || '')
  const [statusMessage, setStatusMessage] = useState(data.statusMessage || '')
  const [assignedEmails, setAssignedEmails] = useState(data.assignedEmails || '')
  const [isLocked, setIsLocked] = useState(data.isLocked || false)
  const [isMinimized, setIsMinimized] = useState(data.isMinimized || false)
  const [isMarkdownExpanded, setIsMarkdownExpanded] = useState(data.isMarkdownExpanded || false)
  const [nodeConfig, setNodeConfig] = useState(data.configuration || { isAutomated: false, modifierprompt: '', markdownFormatVisible: false })
  const [documentStyle, setDocumentStyle] = useState<DocumentStyle>(data.documentStyle || { type: null, value: null })
  const [context, setContext] = useState(data.context || '')
  const [linkedKnowledge, setLinkedKnowledge] = useState(data.linkedKnowledge || '')
  const [isConfigDialogVisible, setIsConfigDialogVisible] = useState(false)
  const [isDocLibraryDialogVisible, setIsDocLibraryDialogVisible] = useState(false)
  const [isUpdateWithAIDialogVisible, setIsUpdateWithAIDialogVisible] = useState(false)
  const [isDataSettingsDialogVisible, setIsDataSettingsDialogVisible] = useState(false)
  const [showDebugInfo, setShowDebugInfo] = useState(false)
  const [copyFeedback, setCopyFeedback] = useState('')

  const syncNodeData = useCallback((newData: Partial<DocumentNodeData>) => {
    if ('selectedSection' in newData) {
      const cleanData = { ...data, selectedSection: newData.selectedSection }
      delete cleanData.data
      updateNodeData(id, { ...cleanData, updatedBy: user.email }) // <-- Add updatedBy
    } else {
      updateNodeData(id, (prevData) => {
        const cleanPrevData = { ...prevData }
        delete cleanPrevData.data
        return { ...cleanPrevData, ...newData, updatedBy: user.email } // <-- Add updatedBy
      })
    }
  }, [id, updateNodeData, data, user.email])

  // --- Sync local state from collaborative state (one field at a time) ---
  useEffect(() => {
    setContentMarkdown(data.contentMarkdown || data.documentDescription || '')
  }, [data.contentMarkdown, data.documentDescription])

  useEffect(() => {
    setStatusMessage(data.statusMessage || '')
  }, [data.statusMessage])

  useEffect(() => {
    setIsMarkdownExpanded(data.isMarkdownExpanded || false)
  }, [data.isMarkdownExpanded])

  useEffect(() => {
    setAssignedEmails(data.assignedEmails || '')
  }, [data.assignedEmails])

  useEffect(() => {
    setIsMinimized(data.isMinimized || false)
  }, [data.isMinimized])

  useEffect(() => {
    setIsLocked(data.isLocked || false)
  }, [data.isLocked])

  useEffect(() => {
    setNodeConfig(data.configuration || { isAutomated: false, modifierprompt: '', markdownFormatVisible: false })
  }, [data.configuration])

  useEffect(() => {
    setDocumentStyle(data.documentStyle || { type: null, value: null })
  }, [data.documentStyle])

  useEffect(() => {
    setContext(data.context || '')
  }, [data.context])

  useEffect(() => {
    setLinkedKnowledge(data.linkedKnowledge || '')
  }, [data.linkedKnowledge])

  const handleLock = () => {
    const newState = !isLocked
    setIsLocked(newState)
    syncNodeData({ isLocked: newState })
  }

  const handleMinimize = () => {
    const newState = !isMinimized
    setIsMinimized(newState)
    syncNodeData({ isMinimized: newState })
  }

  const handleUpdateWithAI = () => {
    if (window.confirm('Are you sure you want to proceed with AI update? This will regenerate the entire document content.')) {
      const documentCommand = createAICommand(AICommandType.DOCUMENT_PROCESS, AICommandStatus.STARTED, {
        mode: 'update-with-ai',
        message: 'Updating document with AI, please wait...'
      })
      syncNodeData({ documentCommand })
      setIsUpdateWithAIDialogVisible(false)
    }
  }

  const handleLoadDocumentTemplates = (combinedMarkdown: string) => {
    syncNodeData({ contentMarkdown: combinedMarkdown, statusMessage: 'Document template(s) loaded successfully' })
  }

  // Updated to accept DocumentStyle directly from DocumentStyleSelector.tsx
  const handleStyleChange = (newStyle: DocumentStyle) => {
    syncNodeData({ documentStyle: newStyle })
  }

  const handleContentChange = (newContent: string) => {
    syncNodeData({ contentMarkdown: newContent, lastModified: Date.now() })
  }

  const handleContextChange = (newContext: string) => {
    if (newContext !== context) {
      syncNodeData({ context: newContext, lastModified: Date.now() })
    }
  }

  const handleLinkedKnowledgeChange = (newLinkedKnowledge: string) => {
    if (newLinkedKnowledge !== linkedKnowledge) {
      syncNodeData({ linkedKnowledge: newLinkedKnowledge, lastModified: Date.now() })
    }
  }

  return {
    contentMarkdown, setContentMarkdown,
    statusMessage, setStatusMessage,
    assignedEmails, setAssignedEmails,
    isLocked, setIsLocked,
    isMinimized, setIsMinimized,
    isMarkdownExpanded, setIsMarkdownExpanded,
    nodeConfig, setNodeConfig,
    documentStyle, setDocumentStyle,
    context, setContext,
    linkedKnowledge, setLinkedKnowledge,
    isConfigDialogVisible, setIsConfigDialogVisible,
    isDocLibraryDialogVisible, setIsDocLibraryDialogVisible,
    isUpdateWithAIDialogVisible, setIsUpdateWithAIDialogVisible,
    isDataSettingsDialogVisible, setIsDataSettingsDialogVisible,
    showDebugInfo, setShowDebugInfo,
    copyFeedback, setCopyFeedback,
    syncNodeData,
    handleLock,
    handleMinimize,
    handleUpdateWithAI,
    handleLoadDocumentTemplates,
    handleStyleChange,
    handleContentChange,
    handleContextChange,
    handleLinkedKnowledgeChange,
  }
}

export default useDocumentState